import { getGlobalMonitor } from '@zg-rentals/monitor-base';
import { tsClientOptions, useQuery } from '@zg-rentals/react-query5';
import { useHpApiInquiryV3Handler_create } from '@zg-rentals/ts-hotpads-api-client-react-query';
import { HpApiInquiryV3Handler_prefill, HpApiWebApiError } from '@zg-rentals/ts-hotpads-api-client';
import type { HpInquiryPrefillDto } from '@zg-rentals/ts-hotpads-api-client';
import { v3ErrorReason } from '@/src/lib/api/client/hotpadsApiV3Client';
import { inquiryKeys } from './queryKeys';
import { USER_LISTINGS_TAG } from './queryTags';

const INQUIRY_TAG = 'inquiry';

// How the endpoint says "nothing to prefill": `notFound` when the renter has no profile or no
// prior inquiry. The v3 client turns every envelope error into a rejection, so absorbing it here
// is what keeps a first-time renter's empty form from failing the query and logging to Splunk.
// `notAuthenticated` is rarer than it looks — callers gate on the cached session — but that cache
// can outlive the real one, so an expired session still lands here rather than in error state.
const EMPTY_PREFILL_REASONS = new Set<HpApiWebApiError>([
  HpApiWebApiError.NOT_AUTHENTICATED,
  HpApiWebApiError.NOT_FOUND,
]);

/**
 * Contact form prefill, sourced from the renter's profile and their most recent inquiry.
 *
 * The cache holds the unwrapped DTO rather than the v3 envelope: `HpApiResponseDto` requires a
 * `session` that no consumer reads and the post-submit `setQueryData` has nothing to put in.
 */
export const useInquiryPrefill = (options?: { enabled?: boolean }) => {
  return useQuery<HpInquiryPrefillDto | null>({
    ...tsClientOptions.queryOptions,
    queryKey: inquiryKeys.prefill(),
    queryFn: async () => {
      try {
        const response = await HpApiInquiryV3Handler_prefill();
        return response.data ?? null;
      } catch (error) {
        const reason = v3ErrorReason(error);
        if (reason && EMPTY_PREFILL_REASONS.has(reason)) {
          return null;
        }
        throw error;
      }
    },
    enabled: options?.enabled,
    staleTime: 0,
    meta: {
      tags: [INQUIRY_TAG],
      context: { api: 'hotpads-api', details: 'fetch inquiry prefill' },
    },
  });
};

/**
 * Submit a listing inquiry through v3.
 *
 * The inquirer email comes from the authenticated session, so the request body carries
 * none — which is why the caller must have a live session before this runs. Envelope
 * failures reject rather than resolving with a flag: branch on `v3ErrorReason`, not on
 * the response. Transport (CSRF header, envelope-error rejection) comes from the client
 * registered by configureHotpadsApiV3Client at bootstrap.
 */
export const useCreateInquiry = () => {
  return useHpApiInquiryV3Handler_create({
    // Metric names mirror legacy hotpads-web (ContactActions) so lead-submission
    // health is comparable across both apps in Marlin during the A/B test.
    onSuccess: () => {
      getGlobalMonitor()?.count({ name: 'ContactForm Submit Success' });
    },
    onError: () => {
      getGlobalMonitor()?.count({ name: 'ContactForm Submit Error' });
    },
    meta: {
      invalidates: [USER_LISTINGS_TAG, INQUIRY_TAG],
      context: { api: 'hotpads-api', details: 'create listing inquiry v3' },
    },
  });
};
